<template>
    <div class="quantity-input">
        <label v-if="label" :for="inputId">{{ label }}</label>
        <input
            :id="inputId"
            type="number"
            :min="min"
            :max="max"
            :step="step"
            :value="modelValue"
            @input="onInput"
            @blur="onBlur"
            :inputmode="decimals > 0 ? 'decimal' : 'numeric'"
            :pattern="decimals > 0 ? '[0-9]+([\\.,][0-9]{1,' + decimals + '})?' : '[0-9]*'"
            class="input"
        />
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    modelValue: {
        type: Number,
        default: 1
    },
    min: {
        type: Number,
        default: 1
    },
    max: {
        type: Number,
        default: 99999
    },
    step: {
        type: Number,
        default: 1
    },
    label: {
        type: String,
        default: ''
    },
    decimals: {
        type: Number,
        default: 0
    }
})

const emit = defineEmits(['update:modelValue'])

function roundToNearestStep(value) {
    // If value equals min, don't round
    if (value === props.min) {
        return value
    }
    
    // Round up to the nearest step
    const steps = Math.ceil((value - props.min) / props.step)
    let rounded = props.min + (steps * props.step)
    
    // Round to specified decimals
    if (props.decimals > 0) {
        rounded = parseFloat(rounded.toFixed(props.decimals))
    }
    
    return rounded
}

function validateAndEmit(value) {
    if (isNaN(value)) value = props.min
    
    // Clamp to min/max
    if (value < props.min) value = props.min
    if (value > props.max) value = props.max
    
    // Round to nearest step, but don't exceed max
    value = roundToNearestStep(value)
    if (value > props.max) value = props.max
    
    emit('update:modelValue', value)
    return value
}

function onInput(event) {
    // Don't validate during typing, just pass the raw value
    let value = parseFloat(event.target.value)
    if (!isNaN(value)) {
        emit('update:modelValue', value)
    }
}

function onBlur(event) {
    let value = parseFloat(event.target.value)
    const correctedValue = validateAndEmit(value)
    // Force update the input to show the corrected value
    event.target.value = correctedValue
}

const inputId = computed(() => `quantity-input-${Math.random().toString(36).substr(2, 9)}`)
</script>

<style scoped>
.quantity-input {
    width: 60px;
    display: flex;
    flex-direction: column;
    max-width: 120px;
}
.input {
    padding: 4px 8px;
    font-size: 1rem;
    text-align: right;
}
</style>

